import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { EMPLOYEE_COLORS, WEEKDAYS } from "@/lib/constants";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Check, Plus, Trash2, ArrowRight, ArrowLeft, PartyPopper, Loader2 } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Onboarding() {
    const { t } = useI18n();
    const { salon, refreshMe } = useAuth();
    const nav = useNavigate();
    const [step, setStep] = useState(0);
    const [saving, setSaving] = useState(false);
    const [data, setData] = useState({
        name: salon?.name || "", address: "", phone: "",
        empName: "", empColor: EMPLOYEE_COLORS[0],
        openingHours: salon?.opening_hours || {},
        services: [{ name: "", duration_min: 30, price: 30 }],
        booking_enabled: true,
    });

    const steps = ["s1", "s3", "s4", "s6", "s7", "s8"];
    const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

    const finish = async () => {
        setSaving(true);
        try {
            await api.patch("/salon", {
                name: data.name, address: data.address, phone: data.phone,
                booking_enabled: data.booking_enabled, onboarding_completed: true,
            });
            if (data.empName.trim()) {
                await api.post("/employees", { name: data.empName, color: data.empColor, active: true });
            }
            for (const s of data.services) {
                if (s.name.trim()) await api.post("/services", { name: s.name, duration_min: Number(s.duration_min), price: Number(s.price) });
            }
            await refreshMe();
            nav("/app/calendar");
        } catch (e) {
            toast.error(formatApiError(e));
            setSaving(false);
        }
    };

    const next = () => (step < steps.length - 1 ? setStep(step + 1) : finish());
    const prev = () => setStep(Math.max(0, step - 1));

    return (
        <div className="min-h-screen bg-secondary/30">
            <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-6"><Logo /><LanguageSwitcher compact /></div>
            <div className="mx-auto max-w-2xl px-6 pb-16">
                <div className="mb-8 flex items-center gap-2">
                    {steps.map((s, i) => (
                        <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${i <= step ? "bg-accent" : "bg-border"}`} />
                    ))}
                </div>
                <div className="rounded-3xl border border-border/70 bg-card p-8 shadow-[0_8px_32px_rgba(0,0,0,0.04)]" data-testid="onboarding-card">
                    <p className="text-sm font-medium text-accent">{t("onboarding.step")} {step + 1} / {steps.length}</p>
                    <h1 className="mt-1 font-serif text-2xl font-bold">{t("onboarding.title")}</h1>

                    <div className="mt-8">
                        {step === 0 && (
                            <div className="space-y-4">
                                <h2 className="font-semibold">{t("onboarding.salonInfo")}</h2>
                                <div><Label>{t("auth.salonName")}</Label><Input value={data.name} onChange={(e) => set("name", e.target.value)} data-testid="ob-salon-name" className="mt-1.5" /></div>
                                <div><Label>Address ({t("common.optional")})</Label><Input value={data.address} onChange={(e) => set("address", e.target.value)} data-testid="ob-address" className="mt-1.5" /></div>
                                <div><Label>{t("common.phone")} ({t("common.optional")})</Label><Input value={data.phone} onChange={(e) => set("phone", e.target.value)} data-testid="ob-phone" className="mt-1.5" /></div>
                            </div>
                        )}
                        {step === 1 && (
                            <div className="space-y-4">
                                <h2 className="font-semibold">{t("onboarding.employeeAdd")}</h2>
                                <div><Label>{t("common.name")}</Label><Input value={data.empName} onChange={(e) => set("empName", e.target.value)} data-testid="ob-emp-name" className="mt-1.5" placeholder="Lisa" /></div>
                            </div>
                        )}
                        {step === 2 && (
                            <div className="space-y-4">
                                <h2 className="font-semibold">{t("onboarding.colorPick")}</h2>
                                <div className="flex flex-wrap gap-3">
                                    {EMPLOYEE_COLORS.map((c) => (
                                        <button key={c} onClick={() => set("empColor", c)} data-testid={`ob-color-${c}`}
                                            className={`flex h-11 w-11 items-center justify-center rounded-full transition-transform duration-200 hover:scale-110 ${data.empColor === c ? "ring-2 ring-offset-2 ring-foreground" : ""}`}
                                            style={{ background: c }}>
                                            {data.empColor === c && <Check className="h-5 w-5 text-white" />}
                                        </button>
                                    ))}
                                </div>
                                <div className="mt-4 flex items-center gap-3 rounded-xl border border-border/70 p-4">
                                    <div className="h-10 w-1.5 rounded-full" style={{ background: data.empColor }} />
                                    <div><p className="text-sm font-semibold">{data.empName || "Lisa"}</p><p className="text-xs text-muted-foreground">10:03 – 10:47</p></div>
                                </div>
                            </div>
                        )}
                        {step === 3 && (
                            <div className="space-y-4">
                                <h2 className="font-semibold">{t("onboarding.servicesAdd")}</h2>
                                {data.services.map((s, i) => (
                                    <div key={i} className="grid grid-cols-[1fr,80px,80px,auto] items-end gap-2">
                                        <div><Label className="text-xs">{t("common.name")}</Label><Input value={s.name} onChange={(e) => { const x = [...data.services]; x[i].name = e.target.value; set("services", x); }} data-testid={`ob-svc-name-${i}`} className="mt-1" placeholder="Knippen" /></div>
                                        <div><Label className="text-xs">Min</Label><Input type="number" value={s.duration_min} onChange={(e) => { const x = [...data.services]; x[i].duration_min = e.target.value; set("services", x); }} className="mt-1" /></div>
                                        <div><Label className="text-xs">€</Label><Input type="number" value={s.price} onChange={(e) => { const x = [...data.services]; x[i].price = e.target.value; set("services", x); }} className="mt-1" /></div>
                                        <Button variant="ghost" size="icon" onClick={() => set("services", data.services.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
                                    </div>
                                ))}
                                <Button variant="outline" size="sm" onClick={() => set("services", [...data.services, { name: "", duration_min: 30, price: 30 }])} className="rounded-full" data-testid="ob-add-service"><Plus className="mr-1 h-4 w-4" />{t("services.newService")}</Button>
                            </div>
                        )}
                        {step === 4 && (
                            <div className="space-y-4">
                                <h2 className="font-semibold">{t("onboarding.bookingToggle")}</h2>
                                <div className="flex items-center justify-between rounded-xl border border-border/70 p-4">
                                    <span className="text-sm">{t("settings.bookingEnabled")}</span>
                                    <Switch checked={data.booking_enabled} onCheckedChange={(v) => set("booking_enabled", v)} data-testid="ob-booking-toggle" />
                                </div>
                            </div>
                        )}
                        {step === 5 && (
                            <div className="py-6 text-center">
                                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-accent/12 text-accent"><PartyPopper className="h-8 w-8" /></div>
                                <h2 className="mt-4 font-serif text-2xl font-bold">{t("onboarding.ready")}</h2>
                                <p className="mt-2 text-muted-foreground">{t("onboarding.readySub")}</p>
                            </div>
                        )}
                    </div>

                    <div className="mt-8 flex items-center justify-between">
                        <Button variant="ghost" onClick={prev} disabled={step === 0} className="rounded-full"><ArrowLeft className="mr-1 h-4 w-4" />{t("common.back")}</Button>
                        <Button onClick={next} disabled={saving} className="rounded-full px-6" data-testid="ob-next">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : step === steps.length - 1 ? <>{t("onboarding.goToCalendar")}<ArrowRight className="ml-1 h-4 w-4" /></> : <>{t("common.next")}<ArrowRight className="ml-1 h-4 w-4" /></>}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
